import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Camera, Eye, EyeOff, Mail, X, Shield } from 'lucide-react';

const AdminLogin = () => {
  const { login, login2FA, isAuthenticated } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUsername, setTwoFAUsername] = useState('');
  const [twoFACode, setTwoFACode] = useState('');

  const from = location.state?.from?.pathname || '/admin/dashboard';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login(formData);
      if (result.success) {
        // Navigation will be handled by the redirect above
      } else if (result.requires_2fa) {
        // Show 2FA input
        setRequires2FA(true);
        setTwoFAUsername(result.username);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login2FA(twoFAUsername, twoFACode);
      if (result.success) {
        // Navigation will be handled by the redirect above
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Invalid 2FA code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${window.location.origin}/api/auth/password-reset-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });
      
      if (response.ok) {
        setResetSent(true);
        setTimeout(() => {
          setShowResetModal(false);
          setResetSent(false);
          setResetEmail('');
        }, 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Ошибка при отправке запроса на сброс пароля');
      }
    } catch (err) {
      setError('Ошибка при отправке запроса на сброс пароля');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            {requires2FA ? (
              <Shield className="h-12 w-12 text-indigo-400" />
            ) : (
              <Camera className="h-12 w-12 text-white" />
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            FotografX Admin
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            {requires2FA ? 'Введите код из приложения-аутентификатора' : 'Sign in to your admin account'}
          </p>
        </div>

        {/* 2FA Form */}
        {requires2FA ? (
          <form className="mt-8 space-y-6" onSubmit={handle2FASubmit}>
            <div>
              <label htmlFor="twofa-code" className="sr-only">
                2FA Code
              </label>
              <input
                id="twofa-code"
                name="twofa-code"
                type="text"
                required
                maxLength={8}
                className="appearance-none rounded-md relative block w-full px-3 py-4 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-400 text-center">
                Также можно использовать резервный код
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-red-900/50 p-4">
                <div className="text-sm text-red-300">{error}</div>
              </div>
            )}

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading || twoFACode.length < 6}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isLoading ? 'Проверка...' : 'Подтвердить'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setTwoFACode('');
                  setError('');
                }}
                className="text-sm text-gray-400 hover:text-white"
              >
                ← Назад к логину
              </button>
            </div>
          </form>
        ) : (
        /* Regular Login Form */
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Username"
                value={formData.username}
                onChange={handleChange}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-900 bg-opacity-50 p-4 border border-red-700">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-300">
                    Login Error
                  </h3>
                  <div className="mt-2 text-sm text-red-200">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowResetModal(true)}
              className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
            >
              Забыли пароль?
            </button>
          </div>

        </form>
        )}

        {/* Password Reset Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4 border border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-white">Сброс пароля</h3>
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setResetSent(false);
                    setResetEmail('');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {!resetSent ? (
                <form onSubmit={handleResetPassword}>
                  <p className="text-gray-300 text-sm mb-4">
                    Введите ваш email адрес, и мы отправим инструкции для сброса пароля.
                  </p>
                  <div className="mb-4">
                    <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-300 mb-2">
                      Email адрес
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        id="resetEmail"
                        type="email"
                        required
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="admin@example.com"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResetModal(false);
                        setResetEmail('');
                      }}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors duration-200"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-md transition-colors duration-200"
                    >
                      {isLoading ? 'Отправка...' : 'Отправить'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center">
                  <div className="mb-4">
                    <Mail className="h-12 w-12 text-green-500 mx-auto mb-2" />
                    <h4 className="text-lg font-medium text-white">Запрос отправлен!</h4>
                  </div>
                  <p className="text-gray-300 text-sm">
                    Если email адрес зарегистрирован в системе, вы получите инструкции для сброса пароля.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;